import { useQuery } from '@tanstack/react-query';
import useCheckoutState from '../hooks/useCheckoutState';
import getAuthToken from '../lib/getAuthToken';
import { checkoutKey, paymentKey } from '../queryKeys';
import api from '../api';
import getSessionId from '../lib/getSessionId';
import getUpsellFunnelStep from '../lib/getUpsellFunnelStep';

export function useInitialCheckout() {
  const { checkout, isUpsell, upsell, isTesting } = useCheckoutState();

  const token = isTesting ? getAuthToken() : null;

  const checkoutSellerId = checkout.seller._id;
  const initialCheckoutId = upsell?.initialCheckoutId;

  return useQuery({
    queryKey: checkoutKey(checkoutSellerId, initialCheckoutId),
    queryFn: () =>
      api.getCheckout(
        checkoutSellerId,
        initialCheckoutId,
        {
          isTesting,
        },
        token,
      ),
    enabled: !!isUpsell && !!initialCheckoutId && !!checkoutSellerId,
  });
}

export function useInitialPayment() {
  const { upsell, isUpsell } = useCheckoutState();

  const { data: initialCheckout } = useInitialCheckout();

  const initialPurchaseId = upsell?.initialPaymentId || upsell?.initialSubscriptionId;
  const initialCheckoutProductType = initialCheckout?.product?.type;

  const sessionId = getSessionId();

  return useQuery({
    queryKey: paymentKey(initialPurchaseId, sessionId),
    queryFn: () =>
      initialCheckoutProductType === 'charge'
        ? api.getCharge(initialPurchaseId, sessionId)
        : api.getSubscription(initialPurchaseId, sessionId),
    enabled: isUpsell && !!initialPurchaseId && !!initialCheckoutProductType,
  });
}

export function useUpsellCheckout() {
  const { checkout, isTesting } = useCheckoutState();

  const token = isTesting ? getAuthToken() : null;

  const upsellFunnelStep = getUpsellFunnelStep(checkout);

  const checkoutSellerId = checkout.seller._id;
  const upsellCheckoutId = upsellFunnelStep?.config?.checkout?._id;

  return useQuery({
    queryKey: checkoutKey(checkoutSellerId, upsellCheckoutId),
    queryFn: () =>
      api.getCheckout(
        checkoutSellerId,
        upsellCheckoutId,
        {
          isTesting,
        },
        token,
      ),
    enabled: !!upsellCheckoutId && !!checkoutSellerId,
  });
}
